import React, { useCallback } from 'react';
import { ColorResult } from 'react-color';

import { useOnKeyDown } from 'common/hooks/useOnKeyDown';

import { SketchWithEyeDropper } from './SketchWithEyeDropper';

// Needs to listen to event on capturing phase, so we can revert the color on Escape before the component is unmounted
const ON_KEY_DOWN_OPTIONS: EventListenerOptions = { capture: true };
const EMPTY_PRESET_COLORS: string[] = [];

type ColorPickerProps = {
  className?: string;
  defaultValue?: string;
  presetColors?: string[];
  onChange?: (color: string) => void;
  onCancel?: () => void;
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
  className,
  defaultValue,
  presetColors,
  onChange,
  onCancel,
}) => {
  const [color, setColor] = React.useState(defaultValue);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel?.();
      }
    },
    [onCancel],
  );

  useOnKeyDown(handleKeyDown, ON_KEY_DOWN_OPTIONS);

  const handleColorChange = React.useCallback((color: ColorResult) => {
    setColor(color.hex);
  }, []);

  const handleColorChangeComplete = React.useCallback(
    (color: ColorResult) => {
      onChange?.(color.hex);
    },
    [onChange],
  );

  return (
    <div className={className} data-role="color-picker">
      <SketchWithEyeDropper
        color={color}
        onChange={handleColorChange}
        onChangeComplete={handleColorChangeComplete}
        presetColors={presetColors ?? EMPTY_PRESET_COLORS}
      />
    </div>
  );
};
